import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '../../api/queryKeys';
import { useInvalidatingMutation } from '../../api/useInvalidatingMutation';
import {
  clearFile,
  clearImage,
  createHomeLink,
  createHomeLinkGroup,
  deleteHomeLink,
  deleteHomeLinkGroup,
  getHomeLink,
  getHomeLinkGroups,
  getHomeLinks,
  updateFile,
  updateHomeLink,
  updateHomeLinkGroup,
  updateImage,
} from './api';
import { HomeLinkDto, HomeLinkGroupDto } from './types';
import { ManufacturingLocation } from '../manufacturingLocation/types';
import { getInventoryCenterIdsByManufacturingLocation } from '../manufacturingLocation/api';

/**
 * Fetches the Home Links of a Manufacturing Location.
 * @param location
 * @param enabled
 * @param refetchInterval
 */
export const useHomeLinksQuery = (
  { inventoryCenterId, mCellId, mCellCollectionId }: ManufacturingLocation,
  enabled = true,
  refetchInterval?: number,
) =>
  useQuery({
    queryKey: [
      QueryKeys.HOME_LINKS,
      inventoryCenterId,
      mCellId,
      mCellCollectionId,
    ],
    queryFn: () =>
      getHomeLinks({ inventoryCenterId, mCellId, mCellCollectionId }),
    enabled,
    refetchInterval,
    meta: { errorMessageKey: 'errorFetchingHomeLinks' },
  });

/**
 * Fetches a Home Link by id.
 * @param id
 */
export const useHomeLinkQuery = (id: string | undefined) =>
  useQuery({
    queryKey: [QueryKeys.HOME_LINK, id],
    queryFn: () => getHomeLink(id!),
    enabled: !!id,
    meta: { errorMessageKey: 'errorFetchingHomeLink' },
  });

/**
 * Fetches the Home Link Groups of the Inventory Centers a Manufacturing Location belongs to.
 * @param location
 * @param enabled
 * @param refetchInterval
 */
export const useHomeLinkGroupsQuery = (
  { inventoryCenterId, mCellId, mCellCollectionId }: ManufacturingLocation,
  enabled = true,
  refetchInterval?: number,
) =>
  useQuery({
    queryKey: [
      QueryKeys.HOME_LINK_GROUPS,
      inventoryCenterId,
      mCellId,
      mCellCollectionId,
    ],
    queryFn: async () =>
      getHomeLinkGroups(
        await getInventoryCenterIdsByManufacturingLocation({
          inventoryCenterId,
          mCellId,
          mCellCollectionId,
        }),
      ),
    enabled,
    refetchInterval,
    meta: { errorMessageKey: 'errorFetchingHomeLinkGroups' },
  });

/**
 * Mutation that changes a Home Link: refetches the Home Link lists and details. The update endpoint's
 * response can carry a stale group name, so the detail is refetched rather than taken from it.
 */
const useHomeLinkMutation = <TVariables>(
  mutationFn: (variables: TVariables) => Promise<HomeLinkDto>,
  errorMessageKey: string,
) =>
  useInvalidatingMutation(
    [QueryKeys.HOME_LINKS],
    mutationFn,
    errorMessageKey,
    undefined,
    [[QueryKeys.HOME_LINK]],
  );

/**
 * Creates a Home Link.
 */
export const useCreateHomeLinkMutation = () =>
  useHomeLinkMutation(createHomeLink, 'errorCreatingHomeLink');

/**
 * Updates a Home Link.
 */
export const useUpdateHomeLinkMutation = () =>
  useHomeLinkMutation(updateHomeLink, 'errorUpdatingHomeLink');

/**
 * Uploads a Home Link's image.
 */
export const useUpdateHomeLinkImageMutation = () =>
  useHomeLinkMutation(updateImage, 'errorUpdatingImage');

/**
 * Removes a Home Link's image.
 */
export const useDeleteHomeLinkImageMutation = () =>
  useHomeLinkMutation(clearImage, 'errorDeletingImage');

/**
 * Uploads a Home Link's file.
 */
export const useUpdateHomeLinkFileMutation = () =>
  useHomeLinkMutation(updateFile, 'errorUpdatingFile');

/**
 * Removes a Home Link's file.
 */
export const useDeleteHomeLinkFileMutation = () =>
  useHomeLinkMutation(clearFile, 'errorDeletingFile');

/**
 * Deletes a Home Link.
 */
export const useDeleteHomeLinkMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.HOME_LINKS],
    deleteHomeLink,
    'errorDeletingHomeLink',
  );

/**
 * Creates a Home Link Group.
 */
export const useCreateHomeLinkGroupMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.HOME_LINK_GROUPS],
    createHomeLinkGroup,
    'errorCreatingHomeLinkGroup',
  );

/**
 * Updates a Home Link Group.
 */
export const useUpdateHomeLinkGroupMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.HOME_LINK_GROUPS],
    updateHomeLinkGroup,
    'errorUpdatingHomeLinkGroup',
  );

/**
 * Deletes a Home Link Group, optimistically removing it from the cached lists.
 */
export const useDeleteHomeLinkGroupMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.HOME_LINK_GROUPS],
    deleteHomeLinkGroup,
    'errorDeletingHomeLinkGroup',
    (groups: HomeLinkGroupDto[], id) => groups.filter((x) => x.id !== id),
  );
